import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Shield, BarChart3, AlertCircle } from 'lucide-react';
import { authedJson } from '../../utils/adminApi';
import StreamsCreatorsEditor from '../../components/dashboard-editors/StreamsCreatorsEditor';
import GalleryEditor from '../../components/dashboard-editors/GalleryEditor';
import StoreEditor from '../../components/dashboard-editors/StoreEditor';
import HomeContentEditor from '../../components/dashboard-editors/HomeContentEditor';

export default function AdminPanel() {
  const [counts, setCounts] = useState({ creators: 0, streams: 0, gallery: 0, store: 0 });
  const [orderStats, setOrderStats] = useState({ orders: 0, paid: 0, revenue: 0 });
  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    const loadCounts = async () => {
      const [creators, streams, gallery, store, orders] = await Promise.all([
        authedJson('/api/admin/creators').catch(() => []),
        authedJson('/api/admin/streams').catch(() => []),
        authedJson('/api/admin/gallery').catch(() => []),
        authedJson('/api/admin/store-products').catch(() => []),
        authedJson('/api/admin/orders').catch(() => [])
      ]);

      setCounts({
        creators: Array.isArray(creators) ? creators.length : 0,
        streams: Array.isArray(streams) ? streams.length : 0,
        gallery: Array.isArray(gallery) ? gallery.length : 0,
        store: Array.isArray(store) ? store.length : 0
      });

      if (Array.isArray(orders)) {
        const paidOrders = orders.filter((o) => o.status === 'paid');
        const revenue = paidOrders.reduce((sum, o) => sum + (o.total_cents || 0), 0);
        setOrderStats({
          orders: orders.length,
          paid: paidOrders.length,
          revenue
        });
        setRecentOrders(orders.slice(0, 5));
      }
    };

    loadCounts();
  }, []);

  const adminStats = [
    {
      label: 'Hall of Fame Entries',
      value: counts.creators.toString(),
      icon: Users,
      change: 'Creators featured',
      color: 'text-[#A1FF4F]'
    },
    {
      label: 'Active Streams',
      value: counts.streams.toString(),
      icon: Shield,
      change: 'Streaming lineup',
      color: 'text-green-400'
    },
    {
      label: 'Gallery Items',
      value: counts.gallery.toString(),
      icon: BarChart3,
      change: 'Moments captured',
      color: 'text-blue-400'
    },
    {
      label: 'Store Items',
      value: counts.store.toString(),
      icon: AlertCircle,
      change: 'Merch live',
      color: 'text-purple-400'
    }
  ];

  return (
    <div>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-8"
      >
        <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
        <p className="text-white/70">Platform management and analytics</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        {adminStats.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: idx * 0.1 }}
              className="bg-[#0A0E11] rounded-xl p-6 border border-[#A1FF4F]/10 hover:border-[#A1FF4F]/30 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-white/70 text-sm">{stat.label}</p>
                  <p className="text-3xl font-bold mt-2">{stat.value}</p>
                </div>
                <Icon className={`w-8 h-8 ${stat.color}`} />
              </div>
              <p className="text-[#A1FF4F] text-sm font-semibold">{stat.change}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <StreamsCreatorsEditor />
        <GalleryEditor />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <motion.div className="bg-[#0A0E11] rounded-xl p-6 border border-[#A1FF4F]/10">
          <p className="text-white/70 text-sm">Total Orders</p>
          <p className="text-3xl font-bold mt-2">{orderStats.orders}</p>
        </motion.div>
        <motion.div className="bg-[#0A0E11] rounded-xl p-6 border border-[#A1FF4F]/10">
          <p className="text-white/70 text-sm">Paid Orders</p>
          <p className="text-3xl font-bold mt-2">{orderStats.paid}</p>
        </motion.div>
        <motion.div className="bg-[#0A0E11] rounded-xl p-6 border border-[#A1FF4F]/10">
          <p className="text-white/70 text-sm">Revenue</p>
          <p className="text-3xl font-bold mt-2">GHS {Math.round(orderStats.revenue / 100)}</p>
        </motion.div>
      </div>

      <div className="mt-6 bg-[#0A0E11] rounded-xl p-6 border border-[#A1FF4F]/10">
        <h3 className="text-xl font-bold mb-4">Recent Orders</h3>
        {recentOrders.length === 0 ? (
          <p className="text-white/60">No orders yet.</p>
        ) : (
          <div className="space-y-3">
            {recentOrders.map((order) => (
              <div key={order.id} className="flex items-center justify-between p-3 bg-white/5 rounded">
                <div>
                  <p className="font-semibold">{order.email}</p>
                  <div className="flex items-center gap-3 text-white/60 text-sm">
                    <span>Status:</span>
                    <select
                      value={order.status}
                      onChange={async (e) => {
                        try {
                          const updated = await authedJson('/api/admin/orders', {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ id: order.id, status: e.target.value })
                          });
                          setRecentOrders(recentOrders.map((o) => (o.id === updated.id ? updated : o)));
                        } catch {
                          // Keep previous value if status update fails.
                        }
                      }}
                      className="bg-black/40 border border-white/10 text-white/80 rounded px-2 py-1"
                    >
                      {['pending', 'paid', 'processing', 'shipped', 'completed', 'cancelled'].map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="text-white/80">GHS {Math.round((order.total_cents || 0) / 100)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <StoreEditor />
        <HomeContentEditor />
      </div>
    </div>
  );
}
